import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/sonner'
import { useCreateMilestoneDateProposalMutation } from '@/redux/collaborationsApi'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: string
  milestoneId: string
  milestoneName: string
  currentDueDate: string | null
}

const addDays = (iso: string | null, days: number): string => {
  const base = iso ? new Date(iso) : new Date()
  base.setDate(base.getDate() + days)
  return base.toISOString().slice(0, 10)
}

const RequestDeadlineExtensionDialog = ({
  open,
  onOpenChange,
  campaignId,
  milestoneId,
  milestoneName,
  currentDueDate,
}: Props) => {
  const [proposedDate, setProposedDate] = useState(() => addDays(currentDueDate, 3))
  const [reason, setReason] = useState('')
  const [createProposal, { isLoading }] = useCreateMilestoneDateProposalMutation()

  const minDate = new Date().toISOString().slice(0, 10)
  const currentLabel = currentDueDate ? new Date(currentDueDate).toISOString().slice(0, 10) : null
  const isInvalid =
    !proposedDate ||
    proposedDate < minDate ||
    (currentLabel != null && proposedDate === currentLabel)

  const handleSubmit = async () => {
    if (isInvalid) return
    try {
      await createProposal({
        campaignId,
        milestoneId,
        proposedDueDate: proposedDate,
        reason: reason.trim() || null,
      }).unwrap()
      toast.success('Request sent', {
        description: 'The brand has been notified — they can accept or suggest a different date.',
      })
      setReason('')
      onOpenChange(false)
    } catch (err: any) {
      console.error('Proposal failed:', err)
      const detail = err?.data?.error
      toast.error(detail || 'Could not submit your request. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#10181D] border border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Request more time</DialogTitle>
          <DialogDescription className="text-gray-400">
            Propose a new deadline for "{milestoneName}". The brand can accept it as the new due
            date or decline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Proposed deadline
            </label>
            <Input
              type="date"
              value={proposedDate}
              min={minDate}
              onChange={(e) => setProposedDate(e.target.value)}
              className="bg-white/5 border-white/15 text-white"
            />
            {currentDueDate && (
              <p className="text-[11px] text-gray-500 mt-1">
                Current deadline:{' '}
                {new Date(currentDueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Reason <span className="text-gray-500 font-normal normal-case">(optional)</span>
            </label>
            <Textarea
              placeholder="Briefly explain why you need more time..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-white/5 border-white/15 text-white placeholder:text-gray-500 min-h-[88px]"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 text-white bg-transparent hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button variant="lime" onClick={handleSubmit} disabled={isInvalid || isLoading}>
            {isLoading ? 'Sending...' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RequestDeadlineExtensionDialog
